/** TYPE values the import datamap accepts (DATAQLib StringToType). */
export const DATAMAP_TYPES = ["CAT", "BOOL", "INT", "REAL", "TEXT", "TIMESTAMP"] as const
export type DatamapType = (typeof DATAMAP_TYPES)[number]

/** One code,label pair from the VALUES cells, kept as text so nothing is lost before validation. */
export interface DatamapValue {
  code: string
  label: string
}

/** One row of the import datamap ($map.csv). `type` is the raw cell; the validator checks it. */
export interface DatamapRow {
  id: string
  label: string
  type: string
  variable: string
  fieldMetadata: string
  values: DatamapValue[]
}

export interface DatamapDoc {
  rows: DatamapRow[]
  /** The file had a FIELD_METADATA column (question text); it is kept on save. */
  hasFieldMetadata: boolean
}

export type DatamapParseResult =
  | { ok: true; doc: DatamapDoc; headerExact: boolean; blankRows: number }
  | { ok: false; error: string }

export type DatamapIssueField = "label" | "type" | "variable" | "values"

export interface DatamapIssue {
  rowId: string
  field: DatamapIssueField
  message: string
}

// The /datamap-check report (DATAQLib CheckDatamapAgainstCSV).
export type ColumnKind = "integer" | "number" | "text" | "empty"

export interface ValueCount {
  value: string
  count: number
}

export interface UnmappedColumnFinding {
  column: string
  /** The datamap variable missing from the data that this column probably is. */
  linkTo: string | null
  /** A mapped variable whose name this column contains (a platform twin such as "<var>oe"). */
  near: string | null
  kind: ColumnKind
  distinct: number
  values: ValueCount[]
  more: boolean
}

export interface MissingColumnFinding {
  variable: string
  labels: string[]
}

export interface CodeCount {
  code: string
  count: number
}

export interface UnknownCodesFinding {
  variable: string
  label: string
  codes: CodeCount[]
  /** The row's codes that the data does use. */
  known: string[]
  more: boolean
}

export interface BadCellFinding {
  variable: string
  label: string
  type: string
  row: number
  cell: string
}

export interface DatamapCheckReport {
  rows: number
  columns: number
  unmappedColumns: UnmappedColumnFinding[]
  missingColumns: MissingColumnFinding[]
  unknownCodes: UnknownCodesFinding[]
  badCells: BadCellFinding[]
}

export type DatamapCheckResponse = DatamapCheckReport | { error: string }
